import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button } from '@/common/components/Button/Button';
import { ControlledTextField } from '@/common/components/Fields/ControlledTextField/ControlledTextField';
import { Form } from '@/common/components/Form/Form';
import { LoadingPlaceholder } from '@/common/components/LoadingPlaceholder/LoadingPlaceholder';
import { PageHeader } from '@/common/components/PageHeader/PageHeader';
import { RouterLink } from '@/common/components/RouterLink/RouterLink';
import { Stack } from '@/common/components/Stack/Stack';
import { Translate } from '@/common/components/Translate/Translate';
import { ROUTES } from '@/common/constants/routes';
import { useAuth } from '@/common/contexts/authentication/AuthProvider';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import {
  SSOAuthenticate,
  SSOAuthorize,
} from '@/common/integrations/menhir/employee/sessions/sessions.service';
import { TSignInUserResponse } from '@/common/integrations/menhir/employee/sessions/sessions.types';
import { AuthFormLayout } from '@/common/layouts/AuthLayout/AuthLayout';
import { notify } from '@/common/utils/toast/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { Href, useLocalSearchParams, useRouter } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { ISSOFormData, SSOFormSchema } from './SSOPage.schema';

export const SSOPage = () => {
  const breakpoints = useBreakpoints(['lg']);
  const { t } = useTranslation();
  const { code, state } = useLocalSearchParams();
  const { signIn, initialRoute } = useAuth();
  const requestedRef = useRef(false);
  const router = useRouter();
  const [authorizing, setAuthorizing] = useState(!!code && !!state);

  const methods = useForm({
    mode: 'onTouched',
    defaultValues: {
      email: '',
    },
    resolver: zodResolver(SSOFormSchema),
  });

  const onSignIn = useCallback(
    async (authResponse: TSignInUserResponse, location: Href) => {
      await signIn(authResponse);
      router.push(location);
    },
    [router, signIn]
  );

  useEffect(() => {
    if (code && state && !requestedRef.current) {
      requestedRef.current = true;
      (async () => {
        try {
          const authResponse = await SSOAuthorize(
            code as string,
            state as string
          );
          try {
            if (initialRoute) {
              await onSignIn(authResponse, initialRoute as Href);
              return;
            }
            await onSignIn(authResponse, {
              pathname: ROUTES.ROOT,
              params: { origin: 'login' },
            });
          } catch {
            await onSignIn(authResponse, ROUTES.PROFILE);
          }
        } catch {
          setAuthorizing(false);
          notify({
            type: 'error',
            message: (
              <Translate i18nKey={'authentication.sso.errors.wrong_email'} />
            ),
          });
        }
      })();
    }
  }, [code, state, onSignIn, initialRoute, t]);

  const onSubmit = async ({ email }: ISSOFormData) => {
    try {
      const { url } = await SSOAuthenticate(email);
      if (url) {
        if (Platform.OS === 'web') {
          window.open(url, '_self');
        } else {
          WebBrowser.openAuthSessionAsync(url, 'coverflex://oauth2/signin');
        }
      }
    } catch {
      notify({
        type: 'error',
        message: (
          <Translate i18nKey={'authentication.sso.errors.wrong_email'} />
        ),
      });
    }
  };

  if (authorizing) {
    return <LoadingPlaceholder />;
  }

  const action = (
    <RouterLink href={ROUTES.SIGNIN}>
      <BodyText underline bold size="sm">
        {t('authentication.sso.link')}
      </BodyText>
    </RouterLink>
  );

  return (
    <AuthFormLayout
      footer={
        <Stack gap="lg">
          <Button
            type="submit"
            loading={methods.formState.isSubmitting}
            onPress={methods.handleSubmit(onSubmit)}
          >
            {t('authentication.sso.confirm_button')}
          </Button>
          {breakpoints.lg && action}
        </Stack>
      }
    >
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <Stack gap="xl">
            <PageHeader
              title={t('authentication.sso.title')}
              description={t('authentication.sso.description')}
            />
            <ControlledTextField
              name="email"
              type="email"
              label={t('authentication.sso.fields.email')}
            />
            {!breakpoints.lg && action}
          </Stack>
        </Form>
      </FormProvider>
    </AuthFormLayout>
  );
};
